import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EmptyState from "../../../components/EmptyState/EmptyState";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import moment from "moment";
import Button from "../../../components/Button";
import ComboBoxUser from "../../../components/ComboBoxUser/ComboBoxUser";
import { Input, Select } from "../../../components/Inputs";
import {
  setOperationHistorySelectedUser,
  setFilteredOperationType,
  setFilteredImportance,
  setFilteredSubject,
  getOperationHistoryListRequest,
} from "../../../redux/actions/operationHistory";
import {
  operationTypes,
  subjects,
  importance,
} from "../../../constants/selects";
import { operationHistoryTrans } from "../../../constants/translations";

const OperationHistory = ({ t }) => {
  const dispatch = useDispatch();
  const { organizationId } = useSelector(
    (state) => state.authorization.user.organization
  );
  const {
    selectedUser,
    filteredOperationType,
    filteredSubject,
    filteredImportance,
    list,
    activePage,
    totalPageCount,
    loading,
  } = useSelector((state) => state.operationHistory);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortedColumn, setSortedColumn] = useState("createDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const size = 10;
  const fieldNames = {
    createDate: "CREATE_DATE",
    organizationName: "ORGANIZATION_NAME",
    userName: "USER_NAME",
    auditImportanceType: "AUDIT_IMPORTANCE_TYPE",
    auditorType: "AUDITOR_TYPE",
    auditType: "AUDIT_TYPE",
    description: "DESCRIPTION",
  };
  const selectOperationType = operationTypes(t);
  const selectSubjects = subjects(t);
  const selectImportance = importance(t);
  const translations = operationHistoryTrans(t);
  const operationHistoryColumns = [
    {
      name: t("pages.default.homepage.table.time"),
      selector: "createDate",
      sortable: true,
      cell: (row) => moment(row.createDate).format("DD.MM.YYYY HH:mm:ss"),
    },
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.operationHistory.table.userName"),
      selector: "userName",
      sortable: true,
    },
    {
      name: t("pages.default.operationHistory.table.importance"),
      selector: "auditImportanceType",
      sortable: true,
      cell: (row) => translations[row.auditImportanceType],
    },
    {
      name: t("pages.default.operationHistory.table.operationType"),
      selector: "auditorType",
      sortable: true,
      cell: (row) => translations[row.auditorType],
    },
    {
      name: t("pages.default.operationHistory.table.subject"),
      selector: "auditType",
      sortable: true,
      cell: (row) => translations[row.auditType],
    },
    {
      name: t("pages.default.operationHistory.table.log"),
      selector: "description",
      sortable: true,
      showTitle: true,
    },
  ];

  useEffect(() => {
    filterOperationHistory(
      activePage,
      sortedColumn,
      sortDirection.toUpperCase()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "operationType":
        dispatch(setFilteredOperationType(value));
        break;
      case "importance":
        dispatch(setFilteredImportance(value));
        break;
      case "subject":
        dispatch(setFilteredSubject(value));
        break;
      default:
        break;
    }
  };

  const filterOperationHistory = (activePage, selector, direction) => {
    dispatch(
      getOperationHistoryListRequest({
        organizationId,
        userId:
          selectedUser && selectedUser.userId ? selectedUser.userId : null,
        auditorType:
          filteredOperationType !== "" ? filteredOperationType : null,
        auditImportanceType:
          filteredImportance !== "" ? filteredImportance : null,
        auditType: filteredSubject !== "" ? filteredSubject : null,
        startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : null,
        endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : null,
        fieldName: fieldNames[selector],
        sortType: direction,
        page: activePage,
        lang: window.localStorage.i18nextLng,
        size,
      })
    );
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.operationHistory.title")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--operation-history-filter-container">
          <ComboBoxUser
            inputPlaceholder={t(
              "pages.default.operationHistory.table.userName"
            )}
            selectedUser={selectedUser}
            userSelect={setOperationHistorySelectedUser}
            organizationId={organizationId}
            id="oh-user"
          />
          <Select
            name="operationType"
            options={selectOperationType}
            defaultValue={filteredOperationType}
            onChange={handleSelectChange}
            id="oh-operationType"
          />
          <Select
            name="importance"
            options={selectImportance}
            defaultValue={filteredImportance}
            onChange={handleSelectChange}
            id="oh-importance"
          />
          <Select
            name="subject"
            options={selectSubjects}
            defaultValue={filteredSubject}
            onChange={handleSelectChange}
            id="oh-subject"
          />
          <Input
            type="date"
            placeholder={t("pages.default.bilateralAgreements.input.startDate")}
            max={new Date(moment(endDate).format("MM-DD-YYYY"))}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            id="oh-startDate"
          />
          <Input
            type="date"
            placeholder={t("pages.default.bilateralAgreements.input.endDate")}
            min={new Date(moment(startDate).format("MM-DD-YYYY"))}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            id="oh-endDate"
          />
          <Button
            label={t("ui.default.filter")}
            onClick={() =>
              filterOperationHistory(
                0,
                sortedColumn,
                sortDirection.toUpperCase()
              )
            }
            id="oh-filter"
          />
        </div>
        <DataTableServerSideRendering
          columns={operationHistoryColumns}
          data={list}
          rowKey="id"
          sortedColumn={sortedColumn}
          setSortedColumn={setSortedColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          pagination
          activePage={activePage}
          totalPageCount={totalPageCount}
          handleFetchData={filterOperationHistory}
        />
        {!loading && list.length === 0 && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.operationHistory.notFound")}
          />
        )}
      </div>
    </>
  );
};

export default OperationHistory;
